import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { KpiSection } from '../../../../../data-models/company-financials.data-model';
import { formatISODateOnly } from '../../../../../util/formatters/DateFormatters';
import { CardList, ICardProps } from '../../../../PortfolioOverview/components/KPISection/CardList';
import { selectedMetricsDateCPState } from '../../../state/CompanyFinancialsDateState';
import { typedFinancialsByPeriodState } from '../../../state/CompanyFinancialsState';
import { selectedCompanyIdProfileAtom } from '../../../state/UIStateJ';
import { perfFrequencyAtom } from '../../../state/FinancialsDateViewPreferences';

import { SubSection } from '../../../Summary/components/SubSection';
import { TypedFinancials } from '../../../utils/getTypedFinancials';
import { RendererType } from '../../../../../data-models/field-definition';
import { FMT } from '../../../../../util/formatter-service';
import { AddStatistic } from './AddStatistic';
import { useKPISelectMenu } from './useKPISelectMenu';

interface KpiData {
  id: number;
  displayName: string;
  valueFormatted: string;
}

export function Statistics() {
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const selectedDate = useAtomValue(selectedMetricsDateCPState(companyId));
  const selectedPeriod = useAtomValue(perfFrequencyAtom(companyId));
  const { handleDelete, handleReorder, selectedFields } = useKPISelectMenu();
  const financials = useAtomValue(
    typedFinancialsByPeriodState({
      companyId,
      date: formatISODateOnly(selectedDate!),
      period: selectedPeriod,
    })
  );
  const financialsByIdAndSection = useMemo(() => getFinancialsByIdAndSection(financials), [financials]);

  const kpis: ICardProps[] = useMemo(() => {
    return selectedFields.map((item) => {
      const financialsById = financialsByIdAndSection?.get(item.id);
      const kpiActual = financialsById?.get(KpiSection.actual);

      return {
        id: String(item.id),
        title: item.value,
        value: applyCustomKpiFormatter(kpiActual) ?? '-',
        indicator: null,
      };
    }, [] as KpiData[]);
  }, [financialsByIdAndSection, selectedFields]);

  return (
    <SubSection title={'Statistics'} actionComponent={<AddStatistic />} noCard>
      <CardList
        kpis={kpis}
        onDelete={handleDelete}
        onReorder={handleReorder}
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(10.5rem, 1fr))' }}
      />
    </SubSection>
  );
}

function getFinancialsByIdAndSection(typedFinancials: TypedFinancials[]) {
  const typedFinancialsByKpiId = new Map<number, Map<KpiSection, TypedFinancials>>();

  typedFinancials.forEach((item) => {
    if (!typedFinancialsByKpiId.has(item.kpiId)) {
      typedFinancialsByKpiId.set(item.kpiId, new Map<KpiSection, TypedFinancials>());
    }
    typedFinancialsByKpiId.get(item.kpiId)!.set(item.section ?? KpiSection.budget, item);
  });

  return typedFinancialsByKpiId;
}

function applyCustomKpiFormatter(kpi: TypedFinancials | undefined): string | undefined {
  if (kpi == null) {
    return undefined;
  }
  if (kpi.rendererType === RendererType.currency) {
    return FMT.format('currencyShort', kpi.value);
  }
  return kpi.valueFormatted;
}
